// User types
export interface User {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  subscription_plan: SubscriptionPlan;
  created_at: string;
  updated_at: string;
}

export type SubscriptionPlan = 'free' | 'starter' | 'professional' | 'enterprise';

// Auth types
export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  full_name: string;
}

// Site types
export interface Site {
  id: string;
  user_id: string;
  domain: string;
  name: string;
  api_key: string;
  secret_key: string;
  is_active: boolean;
  allowed_origins: string[];
  verification_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateSiteDto {
  domain: string;
  name: string;
  allowed_origins?: string[];
}

export interface UpdateSiteDto {
  name?: string;
  is_active?: boolean;
  allowed_origins?: string[];
}

// Analytics types
export interface DashboardStats {
  total_sites: number;
  total_verifications: number;
  verifications_today: number;
  verifications_week: number;
  verifications_month: number;
  success_rate: number;
  monthly_limit: number;
  usage_percentage: number;
}

export interface SiteStats {
  site_id: string;
  total_verifications: number;
  successful_verifications: number;
  failed_verifications: number;
  success_rate: number;
  verifications_by_hour: HourlyStats[];
  recent_verifications: Verification[];
}

export interface DailyStats {
  date: string;
  total: number;
  successful: number;
  failed: number;
}

export interface HourlyStats {
  hour: number;
  count: number;
}

export interface Verification {
  id: string;
  site_id: string;
  status: 'success' | 'failed';
  ip_address: string;
  user_agent: string;
  created_at: string;
}

// Subscription Plan Details
export interface PlanDetails {
  name: string;
  price: number;
  features: string[];
  limit: number;
}

export const PLAN_DETAILS: Record<SubscriptionPlan, PlanDetails> = {
  free: {
    name: 'Free',
    price: 0,
    limit: 1000,
    features: [
      '1,000 verifications/month',
      '1 site',
      'Basic analytics',
      'Community support',
    ],
  },
  starter: {
    name: 'Starter',
    price: 9,
    limit: 10000,
    features: [
      '10,000 verifications/month',
      '5 sites',
      'Full analytics',
      'Email support',
      'Custom widget styling',
    ],
  },
  professional: {
    name: 'Professional',
    price: 29,
    limit: 100000,
    features: [
      '100,000 verifications/month',
      'Unlimited sites',
      'Advanced analytics',
      'Priority support',
      'Custom widget styling',
      'API access',
      'Webhooks',
    ],
  },
  enterprise: {
    name: 'Enterprise',
    price: 99,
    limit: 1000000,
    features: [
      '1,000,000+ verifications/month',
      'Unlimited sites',
      'Enterprise analytics',
      'Dedicated support',
      'Custom integrations',
      'SLA guarantee',
      'On-premise option',
    ],
  },
};

// API Error
export interface ApiError {
  detail: string;
  status_code?: number;
}
